import React, { useMemo, useState } from 'react';
import { useAdminAreas } from '../../hooks/useAdminAreas.js';
import { useLucideIcons } from '../../hooks/usePageAssets.js';

const AreasSection = ({ onNotify }) => {
  const {
    items,
    divisions,
    loading,
    error,
    refresh,
    saveArea,
    deleteArea
  } = useAdminAreas();
  const [search, setSearch] = useState('');
  const [editorState, setEditorState] = useState({ open: false, record: null, saving: false });

  useLucideIcons([items.length, divisions.length, search, editorState.open]);

  const filteredItems = useMemo(() => {
    if (!search) {
      return items;
    }
    const query = search.toLowerCase();
    return items.filter((area) => {
      const name = area.nombre?.toLowerCase() || '';
      const division = area.division_nombre?.toLowerCase() || '';
      const boh = area.boh?.toString() || '';
      return name.includes(query) || division.includes(query) || boh.includes(query);
    });
  }, [items, search]);

  const handleOpenModal = (record = null) => {
    setEditorState({
      open: true,
      saving: false,
      record: record
        ? { ...record }
        : {
          nombre: '',
          boh: '',
          division_id: divisions[0]?.id || '',
          activo: true
        }
    });
  };

  const handleSave = async (values) => {
    setEditorState((prev) => ({ ...prev, saving: true }));
    try {
      await saveArea(values);
      setEditorState({ open: false, record: null, saving: false });
      refresh();
      onNotify?.({
        type: 'success',
        message: values?.id ? 'Área actualizada correctamente.' : 'Área creada correctamente.'
      });
    } catch (err) {
      setEditorState((prev) => ({ ...prev, saving: false }));
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo guardar el área.' });
    }
  };

  const handleDeactivate = async (record) => {
    if (!record.activo) {
      return;
    }
    const confirmed = window.confirm(`¿Desactivar el área "${record.nombre}"?`);
    if (!confirmed) {
      return;
    }
    try {
      await deleteArea(record.id);
      refresh();
      onNotify?.({ type: 'success', message: 'Área desactivada.' });
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo desactivar el área.' });
    }
  };

  const closeModal = () => {
    setEditorState({ open: false, record: null, saving: false });
  };

  return (
    <section id="areas-section" className="admin-section active">
      <div className="section-header">
        <h2 className="section-title">Áreas</h2>
        <div className="section-actions">
          <div className="search-container">
            <span className="search-icon"><i data-lucide="search" /></span>
            <input
              type="text"
              className="form-input"
              placeholder="Buscar por nombre, división o BoH..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {search ? (
              <button type="button" className="btn-secondary" onClick={() => setSearch('')}>
                Limpiar
              </button>
            ) : null}
          </div>
          <button className="btn-primary" type="button" onClick={() => handleOpenModal()}>
            <span className="btn-icon"><i data-lucide="plus" /></span>
            Nueva Área
          </button>
        </div>
      </div>
      <div className="data-table-container">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <div className="loading-text">Cargando áreas...</div>
          </div>
        ) : (
          <table className="data-table" id="areas-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>División</th>
                <th>BoH</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr>
                  <td colSpan={5} className="empty-state">
                    <div className="empty-state-icon"><i data-lucide="alert-triangle" /></div>
                    <div className="empty-state-text">{error}</div>
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={5} className="empty-state">
                    <div className="empty-state-icon"><i data-lucide="map-pin" /></div>
                    <div className="empty-state-text">No hay áreas configuradas.</div>
                    <div className="empty-state-subtext">Crea tu primera área para comenzar.</div>
                  </td>
                </tr>
              ) : (
                filteredItems.map((area) => (
                  <tr key={area.id}>
                    <td>{area.nombre}</td>
                    <td>{area.division_nombre || 'Sin división'}</td>
                    <td>{area.boh}</td>
                    <td>
                      <span className={`status-badge ${area.activo ? 'status-active' : 'status-inactive'}`}>
                        {area.activo ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="action-btn edit" type="button" onClick={() => handleOpenModal(area)}>
                          <i data-lucide="edit" />
                        </button>
                        <button className="action-btn delete" type="button" onClick={() => handleDeactivate(area)}>
                          <i data-lucide="circle-off" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
      {editorState.open ? (
        <AreaModal
          state={editorState}
          divisions={divisions}
          onClose={closeModal}
          onSubmit={handleSave}
        />
      ) : null}
    </section>
  );
};

const AreaModal = ({ state, divisions, onClose, onSubmit }) => {
  const [formValues, setFormValues] = useState(() => ({
    ...state.record,
    nombre: state.record?.nombre || '',
    boh: state.record?.boh ?? '',
    division_id: state.record?.division_id ?? divisions[0]?.id ?? '',
    activo: state.record?.activo ?? true
  }));

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({ ...formValues, id: state.record?.id || null });
  };

  return (
    <div className="modal" style={{ display: 'block' }}>
      <div className="modal-content" style={{ maxWidth: '560px' }}>
        <div className="modal-header">
          <h3>{state.record?.id ? 'Editar Área' : 'Nueva Área'}</h3>
          <button className="modal-close" type="button" onClick={onClose}>
            <i data-lucide="x" />
          </button>
        </div>
        <form className="modal-body form-grid" onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Nombre</label>
            <input
              type="text"
              className="form-input"
              name="nombre"
              value={formValues.nombre}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">BoH</label>
            <input
              type="number"
              className="form-input"
              name="boh"
              value={formValues.boh}
              onChange={handleChange}
              min="0"
              step="1"
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">División</label>
            <select
              className="form-input"
              name="division_id"
              value={formValues.division_id}
              onChange={handleChange}
              required
            >
              <option value="" disabled>Selecciona una división</option>
              {divisions.map((division) => (
                <option key={division.id} value={division.id}>
                  {division.nombre}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group checkbox-group">
            <label className="checkbox-item">
              <input
                type="checkbox"
                name="activo"
                checked={formValues.activo}
                onChange={handleChange}
              />
              <span className="checkmark" />
              <span className="checkbox-label">Área activa</span>
            </label>
          </div>
          <div className="modal-footer">
            <button className="btn-secondary" type="button" onClick={onClose} disabled={state.saving}>
              Cancelar
            </button>
            <button className="btn-primary" type="submit" disabled={state.saving}>
              {state.saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AreasSection;
